import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO 
        title="Privacy Policy - Legend Audiovisual Comics"
        description="Read Legend's privacy policy to understand how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, user privacy, Legend privacy"
        url="https://visual-story-forge.emergent.host/privacy"
        type="article"
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/60 backdrop-blur-xl border-b border-[#FFB966]/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFB966] to-[#FF8C42] blur-md opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex gap-0.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="w-1.5 h-10 bg-gradient-to-b from-[#FFB966] to-[#FF8C42] rounded-full transform rotate-12" style={{ marginLeft: i > 0 ? '2px' : '0' }}></div>
                  ))}
                </div>
              </div>
              <span className="text-2xl font-bold tracking-wider bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">LEGEND</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Privacy <span className="bg-gradient-to-r from-[#FFB966] to-[#FFC57D] bg-clip-text text-transparent">Policy</span>
          </h1>
          <p className="text-gray-400 mb-12">Last updated: April 1, 2026</p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold mb-4 text-white">1. Introduction</h2>
              <p className="leading-relaxed">
                Zing Technologies Inc. ("Legend", "we", "us", or "our") operates the Legend mobile application and website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-white">2. Information We Collect</h2>
              <p className="leading-relaxed mb-4">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Account information (name, email address, username)</li>
                <li>Profile information and preferences</li>
                <li>Content you create or upload (voice recordings, comments)</li>
                <li>Payment information (processed securely through third-party providers)</li>
                <li>Communications with us</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-white">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Personalize and improve your experience</li>
                <li>Monitor and analyze trends and usage</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-white">4. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized or unlawful processing, accidental loss, destruction, or damage.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-white">5. Your Rights</h2>
              <p className="leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access to your personal information</li>
                <li>Correction of inaccurate data</li>
                <li>Deletion of your personal information</li>
                <li>Objection to processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-white">6. Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions about this Privacy Policy, please contact us at:
                <br />
                <a href="mailto:privacy@legendhq.com" className="text-[#FFB966] hover:underline">privacy@legendhq.com</a>
              </p>
            </section>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-gray-500 text-sm">
              © 2026 Zing Technologies Inc. All rights reserved.
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-[#FFB966] transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-[#FFB966] transition-colors">Terms</Link>
              <Link to="/contact" className="text-gray-400 hover:text-[#FFB966] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;